import { LibraryAgentLanguage } from '../library/library-agent-language'
import { LibraryVersion } from '../../helpers/ui-interfaces'

const linkPrefixJava = 'https://search.maven.org'
const linkPrefixPython = 'https://pypi.org'
const linkPrefixDotNet = 'https://www.nuget.org'
const linkPrefixGo = 'https://pkg.go.dev'
const linkDotNetFramework = 'https://dotnet.microsoft.com/download/dotnet/'
const frameWork = 'FRAMEWORK'
const versionRegex = /(-\d+(\.\d+)+(.)*)/i
const versionValidRegex = /(\d+(\.\d+)+(.)*)/i

// Parse a library string to extract what is a piece of a link to the maven repository listing site
const parseLibraryNameJava = ({
  name: nameRaw,
  version: versionRaw
}: {
  readonly name: string
  readonly version?: LibraryVersion
}) => {
  const name = nameRaw.toLowerCase()
  const version = versionRaw?.toLowerCase()

  // If there's no version, remove version from name using regex
  if (!version?.match(versionRegex)) {
    return name.replace(versionRegex, '')
  }

  const index = name.toLowerCase().indexOf(`-${version}`)
  return index >= 0 ? name.slice(0, index) : ''
}

// Build an exact link to a Java library on the maven repository listing site
const buildExactRepoLinkJava = ({
  group,
  namePrefix,
  version
}: {
  readonly group: string
  readonly namePrefix: string
  readonly version: LibraryVersion
}) => `${linkPrefixJava}/artifact/${group}/${namePrefix}/${version}/pom`

// Build a link to the maven repository listing site, with a search query
const buildSearchRepoLinkJava = ({
  name,
  version
}: {
  readonly name: string
  readonly version?: string
}) => {
  const nameOnly = parseLibraryNameJava({ name, version })
  const isValidVersion = !!version?.match(versionValidRegex)
  const query = `name:${nameOnly}${
    !!version && isValidVersion ? ` version:${version}` : ''
  }`

  return `${linkPrefixJava}/search?q=${query}`
}

// Build either an exact link to a maven repository listing or a search query for the library
const buildRepoLinkJava = ({
  group,
  version,
  name
}: {
  readonly group?: string
  readonly version: LibraryVersion
  readonly name: string
}) => {
  if (!!group && !!version && !!name) {
    const namePrefix = parseLibraryNameJava({ name, version })
    if (!!namePrefix) {
      return encodeURI(buildExactRepoLinkJava({ group, namePrefix, version }))
    }
  }
  return !!name ? encodeURI(buildSearchRepoLinkJava({ name, version })) : ''
}

// Build either an exact link to a python dependency listing or a search query for the artifact
const buildRepoLinkPython = ({ name, version }) => {
  if (!!name && !!version && version !== '?') {
    return `${linkPrefixPython}/project/${name}/${version}/`
  } else if (!!name) {
    return `${linkPrefixPython}/search/?q=${name}`
  }
  return ''
}

const buildRepoLinkDotNet = ({ group, version }) => {
  if (!!group && !!version && version !== '?') {
    if (group === frameWork) {
      try {
        const verParts = version.split(' ')[1].split('.')
        const ver = verParts[0] + '.' + verParts[1]
        return linkDotNetFramework + ver
      } catch {
        return linkDotNetFramework
      }
    }
    return `${linkPrefixDotNet}/packages/${group}/${version}`
  }
  return ''
}

const buildRepoLinkGo = ({ name, version }) => {
  if (!!name && !!version && version !== '?') {
    return `${linkPrefixGo}/${name}@${version}`
  }
  return ''
}

export const buildRepoLink = ({
  language,
  group,
  version,
  name
}: {
  language: LibraryAgentLanguage
  group?: string
  version: LibraryVersion
  name: string
}) => {
  switch (language) {
    case LibraryAgentLanguage.JAVA:
      return buildRepoLinkJava({ group, version, name })
    case LibraryAgentLanguage.PYTHON:
      return buildRepoLinkPython({ name, version })
    case LibraryAgentLanguage.DOTNET:
      return buildRepoLinkDotNet({ group, version })
    case LibraryAgentLanguage.GO:
      return buildRepoLinkGo({ name, version })
    default:
      return ''
  }
}
